interface YardiUnitProfile {
  DANNUALRECOVERYPSM?: number;
  DANNUALRENT?: number;
  VALUATIONNETRENT?: number;
  DBUDGETLANDLORDCONTRIBTOFITOUT?: number;
  DBUDGETEDLANDLORDWORKCOST?: number;
  DBUDGETMAKEGOOD?: number;
}

interface YardiUnit {
  YardiUnitProfiles?: YardiUnitProfile[];
}

interface Unit {
  estimated_area?: number;
}

interface DealUnit {
  Unit?: Unit | null;
  VirtualUnit?: Unit | null;
  SavedYardiUnitProfile?: YardiUnitProfile | null;
  YardiUnit?: YardiUnit | null;
}

interface GenericDealData {
  DealsUnits?: DealUnit[];
  budget_outgoings?: number;
  budget_rent?: number;
  base_net_rent?: number;
  outgoings_psm?: number;
  budget_rent_valuation?: number;
  historical_outgoings?: number;
  historical_base_rent?: number;
  budget_incentive?: number;
  budget_lessorworks?: number;
  budget_makegood?: number;
  fitout_contribution?: number;
  cash_incentive?: number;
  DealFitoutLandlordWork?: {
    cost_landlord_works?: number;
    cost_makegood?: number;
  } | null;
}

// Total Budget Gross Rent
export const getTotalBudgetRent = (dealData: GenericDealData): number => {
  const dealUnits = dealData?.DealsUnits?.[0];
  const unit = dealUnits?.Unit ?? dealUnits?.VirtualUnit;

  const budgetInfo =
    dealUnits?.SavedYardiUnitProfile ??
    dealUnits?.YardiUnit?.YardiUnitProfiles?.[0];

  const outgoingsBudget =
    dealData?.budget_outgoings ?? budgetInfo?.DANNUALRECOVERYPSM;

  const area = unit?.estimated_area ?? undefined;
  const areaBudget = area;

  const baseRentBudget = dealData?.budget_rent ?? budgetInfo?.DANNUALRENT;
  return (
    (baseRentBudget ?? 0) + (outgoingsBudget ?? 0) * Number(areaBudget ?? 0)
  );
};

// Total Achieved Gross Rent
export const getTotalAchievedRent = (dealData: GenericDealData): number => {
  const dealUnits = dealData?.DealsUnits?.[0];
  const unit = dealUnits?.Unit ?? dealUnits?.VirtualUnit;
  const baseRent = dealData?.base_net_rent;
  const outgoings = dealData.outgoings_psm ?? undefined;
  const area = unit?.estimated_area ?? undefined;

  return (baseRent ?? 0) + (outgoings ?? 0) * Number(area ?? 0);
};

// Variance between Achieved Rent to Budget Rent
export const getTotalAchievedRentVariance = (
  dealData: GenericDealData
): number => {
  const achieved = getTotalAchievedRent(dealData);
  const budget = getTotalBudgetRent(dealData);
  if (!budget) return 0;
  return ((achieved - budget) / budget) * 100;
};

export const getBaseRentValuation = (dealData: GenericDealData): number => {
  const dealUnits = dealData?.DealsUnits?.[0];

  const budgetInfo =
    dealUnits?.SavedYardiUnitProfile ??
    dealUnits?.YardiUnit?.YardiUnitProfiles?.[0];
  return dealData.budget_rent_valuation ?? budgetInfo?.VALUATIONNETRENT ?? 0;
};

// Variance between Achieved Rent to Valuation Rent
export const getAchievedRentValuationVariance = (
  dealData: GenericDealData
): number => {
  const achieved = getTotalAchievedRent(dealData);
  const valuation = getBaseRentValuation(dealData);
  if (!valuation) return 0;
  return ((achieved - valuation) / valuation) * 100;
};

// Total Current/Previous Rent
export const getTotalCurrentRent = (dealData: GenericDealData): number => {
  return (
    (dealData?.historical_outgoings ?? 0) +
    (dealData?.historical_base_rent ?? 0)
  );
};

// Variance between Achieved Rent to Current/Previous Rent
export const getAchievedToCurrentRentVariance = (
  dealData: GenericDealData
): number => {
  const achieved = getTotalAchievedRent(dealData);
  const current = getTotalCurrentRent(dealData);
  if (!current) return 0;
  return ((achieved - current) / current) * 100;
};

// Total Achieved Total Capital
export const getAchievedTotalCapital = (dealData: GenericDealData): number => {
  const fitout = dealData?.fitout_contribution ?? undefined;
  const landlordWorks =
    dealData?.DealFitoutLandlordWork?.cost_landlord_works ?? undefined;
  const makegood = dealData?.DealFitoutLandlordWork?.cost_makegood ?? undefined;
  const cashIncentive = dealData?.cash_incentive ?? undefined;
  const totalCapitalVars = [makegood, landlordWorks, cashIncentive, fitout];

  let totalCapital: number = 0;
  if (totalCapitalVars.some((a) => a !== undefined)) {
    totalCapital =
      totalCapitalVars.reduce(
        (partialSum, a) => (partialSum ?? 0) + (a ?? 0),
        0
      ) ?? 0;
  }
  return totalCapital;
};

// Total Budget Total Capital
export const getBudgetTotalCapital = (dealData: GenericDealData): number => {
  const dealUnits = dealData?.DealsUnits?.[0];
  const budgetInfo =
    dealUnits?.SavedYardiUnitProfile ??
    dealUnits?.YardiUnit?.YardiUnitProfiles?.[0];

  const budgetCashIncentive =
    dealData?.budget_incentive ??
    budgetInfo?.DBUDGETLANDLORDCONTRIBTOFITOUT ??
    0;
  const budgetLessorWorks =
    dealData?.budget_lessorworks ?? budgetInfo?.DBUDGETEDLANDLORDWORKCOST ?? 0;
  const budgetmakeGood =
    dealData?.budget_makegood ?? budgetInfo?.DBUDGETMAKEGOOD ?? 0;

  return budgetCashIncentive + budgetLessorWorks + budgetmakeGood;
};

// Variance between Achieved Capital to Budget Capital
export const getAchievedToBudgetCapitalVariance = (
  dealData: GenericDealData
): number => {
  const achieved = getAchievedTotalCapital(dealData);
  const budget = getBudgetTotalCapital(dealData);
  if (!budget) return 0;
  return ((achieved - budget) / budget) * 100;
};
